import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Play, Pause, Volume2, VolumeX, Maximize, ArrowLeft, BookOpen, Video, CheckCircle, Clock, List, ChevronDown, ChevronUp, FileText, User, Settings, SkipForward, SkipBack, Check, Rewind, FastForward, Calendar } from 'lucide-react';
import { subjectColors, getSubjectGradient } from '../utils/constants';
import { ScheduleModal } from '../components/ScheduleModal';
import { VideoSolutionsModal } from '../components/VideoSolutionsModal';
import { useProgramContext } from '../contexts/ProgramContext';
const updateLastWatchedVideo = (videoInfo: any, activeVideo: any, progress: number) => {
  if (!activeVideo) return;
  const lastWatchedVideo = {
    id: `video-${activeVideo.id}`,
    subjectId: videoInfo.subject.toLowerCase(),
    subjectTitle: videoInfo.subject,
    unitTitle: videoInfo.title,
    title: activeVideo.title,
    progress: Math.min(Math.round(progress), 100),
    timestamp: Math.floor(progress / 100 * parseTimeToSeconds(activeVideo.duration))
  };
  localStorage.setItem('lastWatchedVideo', JSON.stringify(lastWatchedVideo));
  // Dispatch custom event to notify other components
  window.dispatchEvent(new Event('lastWatchedVideoUpdated'));
};
const parseTimeToSeconds = (timeString: string) => {
  const [mins, secs] = timeString.split(':').map(Number);
  return mins * 60 + secs;
};
export function LessonViewer() {
  const {
    subject = '',
    unit = ''
  } = useParams();
  const navigate = useNavigate();
  const {
    gorevEkle
  } = useProgramContext();
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [progress, setProgress] = useState(0);
  const [showResources, setShowResources] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [watchedVideos, setWatchedVideos] = useState<number[]>([]);
  const videoPlayerRef = useRef<HTMLDivElement>(null);
  const [activeVideoId, setActiveVideoId] = useState<number>(1);
  const [videoWatchThreshold, setVideoWatchThreshold] = useState(95);
  const [showSettingsMenu, setShowSettingsMenu] = useState(false);
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const [videoQuality, setVideoQuality] = useState('720p');
  const settingsRef = useRef<HTMLDivElement>(null);
  // New state for schedule modal
  const [scheduleModalOpen, setScheduleModalOpen] = useState(false);
  const [scheduleItemData, setScheduleItemData] = useState({
    title: '',
    type: 'resource' as 'resource' | 'test' | 'video' | 'homework',
    subject: '',
    resourceId: 0
  });
  // New state for video solutions modal
  const [videoSolutionsModalOpen, setVideoSolutionsModalOpen] = useState(false);
  const [videoSolutionsData, setVideoSolutionsData] = useState({
    title: '',
    videos: [] as {
      id: number;
      title: string;
      duration: string;
      thumbnail?: string;
    }[]
  });
  // Get gradient colors based on subject
  const gradient = getSubjectGradient(subject);
  // Format the unit name for display
  const formatUnitName = (unit: string) => {
    return unit.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
  };
  // Generate unique content based on subject and unit
  const generateUniqueContent = () => {
    const unitName = formatUnitName(unit);
    const subjectName = subject.charAt(0).toUpperCase() + subject.slice(1);
    // Generate unique video information
    let videoInfo = {
      title: unitName,
      subject: subjectName,
      duration: '14:35',
      currentTime: '0:00',
      instructor: 'Dr. Ahmet Yılmaz',
      views: 1245
    };
    // Generate unique related videos based on the topic
    let relatedVideos = [{
      id: 1,
      title: `${unitName} - Temel Kavramlar`,
      duration: '12:30',
      progress: 0
    }, {
      id: 2,
      title: `${unitName} - Tanımlar ve Özellikler`,
      duration: '18:45',
      progress: 0
    }, {
      id: 3,
      title: `${unitName} - Örnek Problemler`,
      duration: '15:20',
      progress: 0
    }, {
      id: 4,
      title: `${unitName} - Çözüm Teknikleri`,
      duration: '14:10',
      progress: 0
    }, {
      id: 5,
      title: `${unitName} - Soru Çözümleri`,
      duration: '20:45',
      progress: 0
    }];
    // Generate unique resources based on the topic
    let resources = [{
      id: 1,
      title: `${unitName} Ders Notu`,
      type: 'PDF',
      size: '2.4 MB'
    }, {
      id: 2,
      title: `${unitName} Soru Bankası`,
      type: 'PDF',
      size: '5.1 MB'
    }, {
      id: 3,
      title: `${unitName} Çalışma Kağıdı`,
      type: 'DOCX',
      size: '1.8 MB'
    }];
    // For specific subjects, customize the content further
    if (subject === 'matematik') {
      if (unit.includes('uslu-sayilar')) {
        relatedVideos = [{
          id: 1,
          title: 'Üslü Sayılar - Tanım ve Özellikler',
          duration: '14:20',
          progress: 0
        }, {
          id: 2,
          title: 'Üslü Sayılar - Çarpma ve Bölme Kuralları',
          duration: '16:35',
          progress: 0
        }, {
          id: 3,
          title: 'Üslü Sayılar - Negatif Üslü İfadeler',
          duration: '12:50',
          progress: 0
        }, {
          id: 4,
          title: 'Üslü Sayılar - Rasyonel Üslü İfadeler',
          duration: '18:15',
          progress: 0
        }, {
          id: 5,
          title: 'Üslü Sayılar - Problemler',
          duration: '22:30',
          progress: 0
        }];
        videoInfo.instructor = 'Prof. Dr. Mehmet Demir';
      } else if (unit.includes('koklu-sayilar')) {
        relatedVideos = [{
          id: 1,
          title: 'Köklü Sayılar - Tanım ve Özellikler',
          duration: '13:40',
          progress: 0
        }, {
          id: 2,
          title: 'Köklü Sayılar - Çarpma ve Bölme İşlemleri',
          duration: '15:25',
          progress: 0
        }, {
          id: 3,
          title: 'Köklü Sayılar - Toplama ve Çıkarma İşlemleri',
          duration: '14:10',
          progress: 0
        }, {
          id: 4,
          title: 'Köklü Sayılar - Kökten Kurtarma Yöntemleri',
          duration: '17:55',
          progress: 0
        }, {
          id: 5,
          title: 'Köklü Sayılar - Problemler',
          duration: '20:15',
          progress: 0
        }];
        videoInfo.instructor = 'Doç. Dr. Ayşe Kaya';
      }
    } else if (subject === 'turkce') {
      if (unit.includes('deyimler-ve-atasozleri')) {
        relatedVideos = [{
          id: 1,
          title: 'Deyimler ve Atasözleri - Tanım ve Özellikleri',
          duration: '11:20',
          progress: 0
        }, {
          id: 2,
          title: 'Deyimler ve Atasözleri - Sık Kullanılan Deyimler',
          duration: '16:45',
          progress: 0
        }, {
          id: 3,
          title: 'Deyimler ve Atasözleri - Sık Kullanılan Atasözleri',
          duration: '15:30',
          progress: 0
        }, {
          id: 4,
          title: 'Deyimler ve Atasözleri - Anlam İncelikleri',
          duration: '13:25',
          progress: 0
        }, {
          id: 5,
          title: 'Deyimler ve Atasözleri - Soru Çözümleri',
          duration: '18:50',
          progress: 0
        }];
        videoInfo.instructor = 'Doç. Dr. Zeynep Yılmaz';
      }
    } else if (subject === 'fen') {
      if (unit.includes('dna-ve-genetik-kod')) {
        relatedVideos = [{
          id: 1,
          title: 'DNA ve Genetik Kod - Temel Kavramlar',
          duration: '15:40',
          progress: 0
        }, {
          id: 2,
          title: 'DNA ve Genetik Kod - DNA Yapısı',
          duration: '17:25',
          progress: 0
        }, {
          id: 3,
          title: 'DNA ve Genetik Kod - Genetik Materyal',
          duration: '14:30',
          progress: 0
        }, {
          id: 4,
          title: 'DNA ve Genetik Kod - Kalıtım',
          duration: '16:15',
          progress: 0
        }, {
          id: 5,
          title: 'DNA ve Genetik Kod - Biyoteknoloji',
          duration: '19:20',
          progress: 0
        }];
        videoInfo.instructor = 'Prof. Dr. Elif Aydın';
      }
    }
    // Update progress for watched videos
    const updatedRelatedVideos = relatedVideos.map(video => {
      if (watchedVideos.includes(video.id)) {
        return {
          ...video,
          progress: 100
        };
      }
      return video;
    });
    return {
      videoInfo,
      relatedVideos: updatedRelatedVideos,
      resources
    };
  };
  // Get unique content
  const {
    videoInfo,
    relatedVideos,
    resources
  } = generateUniqueContent();
  // Close settings menu when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (settingsRef.current && !settingsRef.current.contains(event.target as Node)) {
        setShowSettingsMenu(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  // Handle fullscreen toggle
  const toggleFullscreen = () => {
    if (!videoPlayerRef.current) return;
    if (!isFullscreen) {
      if (videoPlayerRef.current.requestFullscreen) {
        videoPlayerRef.current.requestFullscreen();
      } else if ((videoPlayerRef.current as any).webkitRequestFullscreen) {
        ;
        (videoPlayerRef.current as any).webkitRequestFullscreen();
      } else if ((videoPlayerRef.current as any).msRequestFullscreen) {
        ;
        (videoPlayerRef.current as any).msRequestFullscreen();
      }
      setIsFullscreen(true);
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if ((document as any).webkitExitFullscreen) {
        ;
        (document as any).webkitExitFullscreen();
      } else if ((document as any).msExitFullscreen) {
        ;
        (document as any).msExitFullscreen();
      }
      setIsFullscreen(false);
    }
  };
  // Listen for fullscreen change events
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('msfullscreenchange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('msfullscreenchange', handleFullscreenChange);
    };
  }, []);
  // Handle video selection
  const handleVideoSelect = (videoId: number) => {
    setActiveVideoId(videoId);
    setIsPlaying(true);
    // Reset progress to 0 when selecting a new video
    setProgress(0);
    // Update current time to 0:00
    videoInfo.currentTime = '0:00';
    // Update last watched info
    const selectedVideo = relatedVideos.find(v => v.id === videoId);
    if (selectedVideo) {
      updateLastWatchedVideo(videoInfo, selectedVideo, 0);
    }
  };
  // Handle seeking forward or backward
  const handleSeek = (seconds: number) => {
    // Get total duration in seconds (parse from format like "12:30")
    const activeVideo = relatedVideos.find(v => v.id === activeVideoId);
    if (!activeVideo) return;
    const [mins, secs] = activeVideo.duration.split(':').map(Number);
    const totalDurationInSeconds = mins * 60 + secs;
    // Calculate current time in seconds based on progress
    const currentTimeInSeconds = progress / 100 * totalDurationInSeconds;
    // Calculate new time in seconds after seeking
    let newTimeInSeconds = currentTimeInSeconds + seconds;
    // Ensure new time is within bounds
    newTimeInSeconds = Math.max(0, Math.min(newTimeInSeconds, totalDurationInSeconds));
    // Calculate new progress percentage
    const newProgress = newTimeInSeconds / totalDurationInSeconds * 100;
    // Update progress and current time display
    setProgress(newProgress);
    const minutes = Math.floor(newTimeInSeconds / 60);
    const remainingSeconds = Math.floor(newTimeInSeconds % 60);
    videoInfo.currentTime = `${minutes}:${remainingSeconds < 10 ? '0' : ''}${remainingSeconds}`;
  };
  // Load watched videos from localStorage on component mount
  useEffect(() => {
    const savedWatchedVideos = localStorage.getItem(`watched_${subject}_${unit}`);
    if (savedWatchedVideos) {
      setWatchedVideos(JSON.parse(savedWatchedVideos));
    }
  }, [subject, unit]);
  // Check if all videos are watched
  const allVideosWatched = relatedVideos.length > 0 && relatedVideos.every(video => watchedVideos.includes(video.id));
  // Update topic progress in localStorage when all videos are watched
  useEffect(() => {
    if (allVideosWatched) {
      // Get existing topic progress
      const topicsProgressString = localStorage.getItem('topicsProgress');
      const topicsProgress = topicsProgressString ? JSON.parse(topicsProgressString) : {};
      // Update the progress for this specific topic
      topicsProgress[`${subject}_${unit}`] = 100;
      // Save back to localStorage
      localStorage.setItem('topicsProgress', JSON.stringify(topicsProgress));
    }
  }, [allVideosWatched, subject, unit]);
  // Format time display (seconds to MM:SS)
  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    return `${minutes}:${remainingSeconds < 10 ? '0' : ''}${remainingSeconds}`;
  };
  // Simulate video progress
  useEffect(() => {
    if (isPlaying) {
      // Get total duration in seconds (parse from format like "12:30")
      const activeVideo = relatedVideos.find(v => v.id === activeVideoId);
      if (!activeVideo) return;
      const [mins, secs] = activeVideo.duration.split(':').map(Number);
      const totalDurationInSeconds = mins * 60 + secs;
      // Adjust timer based on playback speed
      const timerInterval = 300 / playbackSpeed; // Faster playback = faster timer
      const timer = setInterval(() => {
        setProgress(prev => {
          // Calculate new progress percentage, adjusted for playback speed
          const newProgress = prev + 100 / (totalDurationInSeconds * 2) * playbackSpeed;
          // Update current time display - FIX: Properly format time with leading zeros
          const currentTimeInSeconds = newProgress / 100 * totalDurationInSeconds;
          const minutes = Math.floor(currentTimeInSeconds / 60);
          const seconds = Math.floor(currentTimeInSeconds % 60);
          videoInfo.currentTime = `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
          // Update last watched info in localStorage
          updateLastWatchedVideo(videoInfo, activeVideo, newProgress);
          if (newProgress >= 100) {
            clearInterval(timer);
            setIsPlaying(false);
            // Only mark as watched when it reaches 100%
            if (!watchedVideos.includes(activeVideoId)) {
              const newWatchedVideos = [...watchedVideos, activeVideoId];
              setWatchedVideos(newWatchedVideos);
              localStorage.setItem(`watched_${subject}_${unit}`, JSON.stringify(newWatchedVideos));
            }
            return 100;
          }
          // Only mark as watched when it reaches the threshold
          if (newProgress >= videoWatchThreshold && !watchedVideos.includes(activeVideoId)) {
            const newWatchedVideos = [...watchedVideos, activeVideoId];
            setWatchedVideos(newWatchedVideos);
            localStorage.setItem(`watched_${subject}_${unit}`, JSON.stringify(newWatchedVideos));
          }
          return newProgress;
        });
      }, timerInterval);
      return () => clearInterval(timer);
    }
  }, [isPlaying, activeVideoId, watchedVideos, subject, unit, videoWatchThreshold, playbackSpeed]);
  // Handle adding item to schedule
  const handleAddToSchedule = (data: {
    title: string;
    subject: string;
    type: string;
    day: string;
    startTime: string;
    endTime: string;
    notes?: string;
  }) => {
    // Get the active video or resource based on schedule item type
    const activeVideo = relatedVideos.find(v => v.id === activeVideoId);
    const activeResource = scheduleItemData.resourceId ? resources.find(r => r.id === scheduleItemData.resourceId) : null;
    // Create additional details based on activity type
    let videoDetay = '';
    let kaynakDetay = '';
    let testDetay = '';
    let etkinlikTipi = data.type;
    if (data.type === 'video' && activeVideo) {
      videoDetay = `${activeVideo.id}/${relatedVideos.length} Video`;
      etkinlikTipi = 'video'; // Ensure activity type is 'video'
    } else if (data.type === 'resource' && activeResource) {
      kaynakDetay = activeResource.title;
      etkinlikTipi = 'resource'; // Ensure activity type is 'resource'
    } else if (data.type === 'test' || data.title.includes('Soru Çözüm Videosu')) {
      testDetay = 'Soru Çözüm Videosu';
      // If it's a video solution, change the type to ensure proper labeling
      etkinlikTipi = 'test'; // Ensure activity type is 'test'
    }
    // Convert to the format expected by ProgramContext
    gorevEkle({
      dersAdi: videoInfo.subject.toUpperCase(),
      videoAdi: data.title,
      konuAdi: videoInfo.title,
      gun: data.day,
      saatAraligi: `${data.startTime}-${data.endTime}`,
      notlar: data.notes,
      tamamlandi: false,
      etkinlikTipi: etkinlikTipi,
      videoDetay,
      kaynakDetay,
      testDetay
    });
    // Show success message or notification here if needed
    alert(`${data.title} programınıza eklendi!`);
    setScheduleModalOpen(false);
  };
  // Handle opening video solutions modal
  const handleOpenVideoSolutions = (title: string, videoCount: number) => {
    // Generate sample video solutions based on the title and count
    const solutionVideos = Array.from({
      length: videoCount
    }, (_, i) => ({
      id: i + 1,
      title: title,
      duration: `${Math.floor(Math.random() * 10) + 3}:${Math.floor(Math.random() * 60).toString().padStart(2, '0')}`,
      thumbnail: 'https://images.unsplash.com/photo-1456513080510-7bf3a84b82f8?w=800&h=450&fit=crop&crop=faces&q=80',
      description: `Bu videoda ${videoInfo.title} konusuna ait soru çözümleri bulunmaktadır. Çözümlü sorular ile konuyu pekiştirebilirsiniz.`
    }));
    setVideoSolutionsData({
      title: `${title}`,
      videos: solutionVideos
    });
    setVideoSolutionsModalOpen(true);
  };
  return <div className="space-y-6" data-id="element-2127">
      <div className="flex items-center gap-4 mb-6" data-id="element-2128">
        <button onClick={() => navigate('/topics')} className={`flex items-center gap-2 px-4 py-2 rounded-lg border bg-gradient-to-r ${gradient.from} ${gradient.to} text-white hover:opacity-90 transition-all`} data-id="element-2129">
          <ArrowLeft className="w-5 h-5" data-id="element-2130" />
          Geri
        </button>
        <h1 className="text-2xl font-bold" data-id="element-2131">
          {videoInfo.subject} - {videoInfo.title}
        </h1>
        {allVideosWatched && <span className="px-3 py-1 bg-green-500/20 text-green-400 rounded-full text-sm flex items-center gap-1" data-id="element-2132">
            <CheckCircle className="w-4 h-4" data-id="element-2133" />
            Tamamlandı
          </span>}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6" data-id="element-2134">
        {/* Main Video Column (60%) */}
        <div className="lg:col-span-3 space-y-6" data-id="element-2135">
          {/* Video Player */}
          <div className="relative p-[1px] rounded-xl overflow-hidden bg-gradient-to-r from-purple-600 to-indigo-600" data-id="element-2136">
            <div className="relative bg-black/90 rounded-xl" data-id="element-2137">
              {/* Video Container */}
              <div ref={videoPlayerRef} className="relative aspect-video bg-black" data-id="element-2138">
                <div className="absolute inset-0 flex items-center justify-center" data-id="element-2139">
                  <img src="https://images.unsplash.com/photo-1456513080510-7bf3a84b82f8?w=800&h=450&fit=crop&crop=faces&q=80" alt="Video thumbnail" className="w-full h-full object-cover opacity-70" data-id="element-2140" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" data-id="element-2141"></div>
                </div>
                {/* Video Controls Overlay */}
                <div className="absolute inset-0 flex flex-col justify-end p-4" data-id="element-2142">
                  <div className="mb-2" data-id="element-2143">
                    <div className="relative h-1 bg-white/30 rounded-full" data-id="element-2144">
                      <div className="absolute h-full bg-gradient-to-r from-purple-600 to-indigo-600 rounded-full" style={{
                      width: `${progress}%`
                    }} data-id="element-2145"></div>
                      <div className="absolute h-3 w-3 bg-white rounded-full -mt-1 shadow-sm" style={{
                      left: `${progress}%`
                    }} data-id="element-2146"></div>
                    </div>
                  </div>
                  <div className="flex justify-between items-center" data-id="element-2147">
                    <div className="flex items-center gap-3" data-id="element-2148">
                      <button onClick={() => handleSeek(-10)} className="p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors" title="10 saniye geri" data-id="element-2149">
                        <Rewind className="w-5 h-5" data-id="element-2150" />
                      </button>
                      <button onClick={() => setIsPlaying(!isPlaying)} className="p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors" data-id="element-2151">
                        {isPlaying ? <Pause className="w-5 h-5" data-id="element-2152" /> : <Play className="w-5 h-5" data-id="element-2153" />}
                      </button>
                      <button onClick={() => handleSeek(10)} className="p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors" title="10 saniye ileri" data-id="element-2154">
                        <FastForward className="w-5 h-5" data-id="element-2155" />
                      </button>
                      <button onClick={() => setIsMuted(!isMuted)} className="p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors" data-id="element-2156">
                        {isMuted ? <VolumeX className="w-5 h-5" data-id="element-2157" /> : <Volume2 className="w-5 h-5" data-id="element-2158" />}
                      </button>
                      <span className="text-sm ml-2" data-id="element-2159">
                        {videoInfo.currentTime} /{' '}
                        {relatedVideos.find(v => v.id === activeVideoId)?.duration || videoInfo.duration}
                      </span>
                    </div>
                    <div className="flex items-center gap-2" data-id="element-2160">
                      {/* Settings button with dropdown */}
                      <div className="relative" ref={settingsRef} data-id="element-2161">
                        <button className="p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors" onClick={() => setShowSettingsMenu(!showSettingsMenu)} data-id="element-2162">
                          <Settings className="w-5 h-5" data-id="element-2163" />
                        </button>
                        {showSettingsMenu && <div className="absolute bottom-12 right-0 bg-black/95 border border-gray-800 rounded-lg shadow-lg p-2 min-w-[200px] z-50" data-id="element-2164">
                            {/* Playback Speed */}
                            <div className="p-2" data-id="element-2165">
                              <p className="text-sm text-gray-400 mb-2" data-id="element-2166">
                                Oynatma Hızı
                              </p>
                              <div className="grid grid-cols-4 gap-1" data-id="element-2167">
                                {[0.5, 1, 1.5, 2].map(speed => <button key={speed} className={`px-2 py-1 text-sm rounded ${playbackSpeed === speed ? 'bg-purple-600 text-white' : 'bg-white/10 text-gray-300 hover:bg-white/20'}`} onClick={() => setPlaybackSpeed(speed)} data-id="element-2168">
                                    {speed}x
                                  </button>)}
                              </div>
                            </div>
                            {/* Video Quality */}
                            <div className="p-2 border-t border-gray-800 mt-1" data-id="element-2169">
                              <p className="text-sm text-gray-400 mb-2" data-id="element-2170">
                                Kalite
                              </p>
                              <div className="space-y-1" data-id="element-2171">
                                {['480p', '720p', '1080p'].map(quality => <button key={quality} className={`flex items-center gap-2 w-full px-3 py-1.5 text-sm rounded text-left ${videoQuality === quality ? 'bg-purple-600 text-white' : 'hover:bg-white/10 text-gray-300'}`} onClick={() => setVideoQuality(quality)} data-id="element-2172">
                                    {quality}
                                    {videoQuality === quality && <Check className="w-4 h-4 ml-auto" data-id="element-2173" />}
                                  </button>)}
                              </div>
                            </div>
                          </div>}
                      </div>
                      <button className="p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors" onClick={toggleFullscreen} data-id="element-2174">
                        <Maximize className="w-5 h-5" data-id="element-2175" />
                      </button>
                    </div>
                  </div>
                </div>
                {/* Center Play Button */}
                {!isPlaying && <button onClick={() => setIsPlaying(true)} className="absolute inset-0 flex items-center justify-center" data-id="element-2176">
                    <div className="p-4 rounded-full bg-white/20 backdrop-blur-sm hover:bg-white/30 transition-colors" data-id="element-2177">
                      <Play className="w-10 h-10" fill="white" data-id="element-2178" />
                    </div>
                  </button>}
              </div>
              {/* Simplified Video Info */}
              <div className="p-6" data-id="element-2179">
                <h2 className="text-xl font-semibold mb-2" data-id="element-2180">
                  {relatedVideos.find(v => v.id === activeVideoId)?.title || videoInfo.title}
                </h2>
                <div className="flex flex-wrap gap-4 text-sm text-gray-400" data-id="element-2181">
                  <div className="flex items-center gap-1" data-id="element-2182">
                    <BookOpen className="w-4 h-4" data-id="element-2183" />
                    <span data-id="element-2184">{videoInfo.subject}</span>
                  </div>
                  <div className="flex items-center gap-1" data-id="element-2185">
                    <Clock className="w-4 h-4" data-id="element-2186" />
                    <span data-id="element-2187">
                      {relatedVideos.find(v => v.id === activeVideoId)?.duration || videoInfo.duration}
                    </span>
                  </div>
                  <div className="flex items-center gap-1" data-id="element-2188">
                    <User className="w-4 h-4" data-id="element-2189" />
                    <span data-id="element-2190">{videoInfo.instructor}</span>
                  </div>
                  <div className="flex items-center gap-1 ml-auto" data-id="element-2191">
                    <span className="text-xs px-2 py-0.5 bg-purple-500/20 text-purple-400 rounded-full" data-id="element-2192">
                      {videoQuality}
                    </span>
                    <span className="text-xs px-2 py-0.5 bg-purple-500/20 text-purple-400 rounded-full" data-id="element-2193">
                      {playbackSpeed}x
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* Resources Section */}
          <div className="relative p-[1px] rounded-xl overflow-hidden bg-gradient-to-r from-purple-600 to-indigo-600" data-id="element-2194">
            <div className="relative bg-black/90 rounded-xl" data-id="element-2195">
              <div className="p-6" data-id="element-2196">
                <div className="flex justify-between items-center cursor-pointer" onClick={() => setShowResources(!showResources)} data-id="element-2197">
                  <h2 className="text-xl font-semibold" data-id="element-2198">Kaynaklar</h2>
                  <button className="p-2 rounded-lg bg-white/5 hover:bg-white/10 transition-colors" data-id="element-2199">
                    {showResources ? <ChevronUp className="w-5 h-5" data-id="element-2200" /> : <ChevronDown className="w-5 h-5" data-id="element-2201" />}
                  </button>
                </div>
                {showResources && <div className="mt-4 space-y-3 custom-scrollbar" data-id="element-2202">
                    {resources.map(resource => <div key={resource.id} className="flex items-center justify-between p-3 bg-white/5 rounded-lg hover:bg-white/10 transition-colors" data-id="element-2203">
                        <div className="flex items-center gap-3" data-id="element-2204">
                          <div className="p-2 bg-purple-500/20 text-purple-400 rounded-lg" data-id="element-2205">
                            <FileText className="w-5 h-5" data-id="element-2206" />
                          </div>
                          <div data-id="element-2207">
                            <h3 className="font-medium" data-id="element-2208">{resource.title}</h3>
                            <p className="text-sm text-gray-400" data-id="element-2209">
                              {resource.type} • {resource.size}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2" data-id="element-2210">
                          <button className="p-2 rounded-lg bg-white/5 hover:bg-white/10 transition-colors" onClick={e => {
                      e.stopPropagation();
                      setScheduleModalOpen(true);
                      setScheduleItemData({
                        title: `Kaynak Çalışması: ${resource.title}`,
                        type: 'resource',
                        subject: videoInfo.subject,
                        resourceId: resource.id
                      });
                    }} title="Programa Ekle" data-id="element-2211">
                            <Calendar className="w-5 h-5 text-purple-400 hover:text-white" data-id="element-2212" />
                          </button>
                          <button className="p-2 rounded-lg bg-white/5 hover:bg-white/10 transition-colors" data-id="element-2213">
                            <FileText className="w-5 h-5 text-gray-400 hover:text-white" data-id="element-2214" />
                          </button>
                        </div>
                      </div>)}
                  </div>}
              </div>
            </div>
          </div>
        </div>

        {/* Sidebar Column (40%) - Playlist */}
        <div className="lg:col-span-2 space-y-6" data-id="element-2215">
          {/* Related Videos */}
          <div className="relative p-[1px] rounded-xl overflow-hidden bg-gradient-to-r from-purple-600 to-indigo-600" data-id="element-2216">
            <div className="relative bg-black/90 rounded-xl" data-id="element-2217">
              <div className="p-6" data-id="element-2218">
                <div className="flex justify-between items-center mb-4" data-id="element-2219">
                  <h2 className="text-xl font-semibold" data-id="element-2220">Konu Videoları</h2>
                  <span className="text-sm text-gray-400" data-id="element-2221">
                    {watchedVideos.length}/{relatedVideos.length} tamamlandı
                  </span>
                </div>
                <div className="space-y-4 custom-scrollbar max-h-[500px] overflow-y-auto pr-2" data-id="element-2222">
                  {relatedVideos.map(video => {
                  const isWatched = watchedVideos.includes(video.id);
                  const isActive = activeVideoId === video.id;
                  return <div key={video.id} className={`flex gap-3 p-3 rounded-lg transition-colors cursor-pointer ${isActive ? 'bg-white/15 ring-1 ring-purple-500' : 'bg-white/5 hover:bg-white/10'}`} data-id="element-2223">
                        <div className="relative w-24 h-16 rounded-lg overflow-hidden flex-shrink-0" onClick={() => handleVideoSelect(video.id)} data-id="element-2224">
                          <img src="https://images.unsplash.com/photo-1456513080510-7bf3a84b82f8?w=150&h=150&fit=crop&crop=faces&q=80" alt={video.title} className="w-full h-full object-cover" data-id="element-2225" />
                          <div className="absolute inset-0 flex items-center justify-center" data-id="element-2226">
                            {isWatched ? <div className="p-1 rounded-full bg-green-500/80" data-id="element-2227">
                                <CheckCircle className="w-4 h-4" data-id="element-2228" />
                              </div> : <div className="p-1 rounded-full bg-black/50" data-id="element-2229">
                                <Play className="w-4 h-4" fill="white" data-id="element-2230" />
                              </div>}
                          </div>
                        </div>
                        <div className="flex-1 min-w-0" onClick={() => handleVideoSelect(video.id)} data-id="element-2231">
                          <h3 className="font-medium text-sm line-clamp-2" data-id="element-2232">
                            {video.title}
                          </h3>
                          <div className="flex items-center gap-2 mt-2 text-xs text-gray-400" data-id="element-2233">
                            <Clock className="w-3 h-3" data-id="element-2234" />
                            <span data-id="element-2235">{video.duration}</span>
                            {isWatched && <>
                                <span className="text-gray-500" data-id="element-2236">•</span>
                                <span className="text-green-400" data-id="element-2237">
                                  Tamamlandı
                                </span>
                              </>}
                          </div>
                        </div>
                        <button className="p-2 self-center rounded-lg bg-white/5 hover:bg-white/10 transition-colors" onClick={e => {
                      e.stopPropagation();
                      setScheduleModalOpen(true);
                      setScheduleItemData({
                        title: `Video İzleme: ${video.title}`,
                        type: 'video',
                        subject: videoInfo.subject,
                        videoId: video.id
                      });
                    }} title="Programa Ekle" data-id="element-2238">
                          <Calendar className="w-4 h-4 text-purple-400 hover:text-white" data-id="element-2239" />
                        </button>
                      </div>;
                })}
                </div>
              </div>
            </div>
          </div>

          {/* Video Solutions (formerly Practice Questions) */}
          <div className="relative p-[1px] rounded-xl overflow-hidden bg-gradient-to-r from-purple-600 to-indigo-600" data-id="element-2240">
            <div className="relative bg-black/90 rounded-xl p-6" data-id="element-2241">
              <h2 className="text-xl font-semibold mb-4" data-id="element-2242">
                Soru Çözüm Videoları
              </h2>
              <div className="space-y-3" data-id="element-2243">
                {[1, 2, 3, 4].map(num => <div key={num} className="w-full flex items-center justify-between p-4 bg-white/5 rounded-lg hover:bg-white/10 transition-colors cursor-pointer" onClick={() => handleOpenVideoSolutions(`Soru Çözüm Videosu ${num}`, 1)} data-id="element-2244">
                    <div className="flex items-center gap-3" data-id="element-2245">
                      <div className="p-2 bg-purple-500/20 text-purple-400 rounded-lg" data-id="element-2246">
                        <Video className="w-5 h-5" data-id="element-2247" />
                      </div>
                      <span data-id="element-2248">Soru Çözüm Videosu {num}</span>
                    </div>
                    <div className="flex items-center gap-2" data-id="element-2249">
                      <button className="p-2 rounded-lg bg-white/5 hover:bg-white/10 transition-colors" onClick={e => {
                    e.stopPropagation();
                    setScheduleModalOpen(true);
                    setScheduleItemData({
                      title: `Soru Çözüm Videosu İzleme: Soru Çözüm Videosu ${num}`,
                      type: 'video',
                      subject: videoInfo.subject,
                      videoId: num
                    });
                  }} title="Programa Ekle" data-id="element-2250">
                        <Calendar className="w-5 h-5 text-purple-400 hover:text-white" data-id="element-2251" />
                      </button>
                      <span className="text-sm text-gray-400" data-id="element-2252">
                        {Math.floor(Math.random() * 10) + 5}:
                        {Math.floor(Math.random() * 60).toString().padStart(2, '0')}
                      </span>
                    </div>
                  </div>)}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Schedule Modal */}
      {scheduleModalOpen && <ScheduleModal initialData={scheduleItemData} onClose={() => setScheduleModalOpen(false)} onSave={data => {
      // Handle saving to schedule
      handleAddToSchedule(data);
      setScheduleModalOpen(false);
    }} data-id="element-2253" />}

      {/* Video Solutions Modal */}
      {videoSolutionsModalOpen && <VideoSolutionsModal videos={videoSolutionsData.videos} title={videoSolutionsData.title} onClose={() => setVideoSolutionsModalOpen(false)} data-id="element-2254" />}
    </div>;
}